import { createAsyncThunk, createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit'
import { apiStatus } from 'shared/constants/AppEnum'
import { offeringPoolAPI } from 'api/temple'
import { AppState } from 'store/reduxStore'
import { TempleIdType } from 'types/api/temple'
import { IApiState } from 'store/types'
import { selectCurrentTemple, selectCurrentTempleId } from './templeSlice'

// Request payload type
export interface OfferingPoolRequestType {
    poolName: string;
    poolLimit?: string;
}

// Response type
export interface OfferingPoolResponseType {
    url: string;
    id: number;
    deleted: string | null;
    deletedByCascade: boolean;
    poolName: string;
    poolLimit: number;
    temple: string;
}


const offeringPoolAdapter = createEntityAdapter<OfferingPoolResponseType>({
    sortComparer: (a, b) => a.poolName.localeCompare(b.poolName),
    selectId: (obj) => obj.url
})

type InitialStateType = {
    api: IApiState
    loadedTempleIds: Record<string, boolean>
    loadingTempleIds: Record<string, boolean>
}

const addOnIntitalState: InitialStateType = {
    api: {
        status: apiStatus.IDLE,
        error: null
    },
    loadedTempleIds: {},
    loadingTempleIds: {},
}



export const offeringPoolListThunk = createAsyncThunk(
    'offeringPool/list',
    async (payload: TempleIdType, { rejectWithValue, getState }) => {
        let templeId = payload.templeId ?? selectCurrentTempleId(getState() as AppState)
        try {
            const response = await offeringPoolAPI.list(templeId)
            return { templeId, items: response.data }
        } catch (err: any) {
            return rejectWithValue(err.response.data)
        }
    },
    {
        condition: (payload, { getState }) => {
            const state = getState() as AppState
            const templeId = payload.templeId ?? selectCurrentTempleId(state)
            if (!templeId) return false
            return !state.offeringPool.loadedTempleIds[String(templeId)] && !state.offeringPool.loadingTempleIds[String(templeId)]
        }
    }
)

export const offeringPoolCreateThunk = createAsyncThunk(
    'offeringPool/create',
    async (payload: OfferingPoolRequestType & TempleIdType, { rejectWithValue, getState }) => {
        let templeId = payload.templeId ?? selectCurrentTempleId(getState() as AppState)
        try {
            const response = await offeringPoolAPI.post(payload, templeId)
            return response.data
        } catch (err: any) {
            return rejectWithValue(err.response.data)
        }
    }
)

export const offeringPoolUpdateThunk = createAsyncThunk(
    'offeringPool/update',
    async (payload: PayloadWithUrl<Partial<OfferingPoolRequestType>>, { rejectWithValue }) => {
        try {
            const response = await offeringPoolAPI.update(payload.url, payload.data)
            return response.data
        } catch (err: any) {
            return rejectWithValue(err.response.data)
        }
    }
)

export const offeringPoolRetrieveThunk = createAsyncThunk(
    'offeringPool/retrieve',
    async (payload: IdOrUrlType & TempleIdType, { rejectWithValue, getState }) => {
        let templeId = payload.templeId ?? selectCurrentTempleId(getState() as AppState)
        try {
            let params
            if ('id' in payload && payload.id)
                params = { itemId: payload.id }
            else if ('url' in payload)
                params = { url: payload.url }
            else
                return rejectWithValue({ detail: 'Identifier not provided' })
            const response = await offeringPoolAPI.get(params, templeId)
            return response.data
        } catch (err: any) {
            return rejectWithValue(err.response.data)
        }
    }
)

export const offeringPoolDeleteThunk = createAsyncThunk(
    'offeringPool/delete',
    async (payload: IdAndUrlType) => {
        await offeringPoolAPI.delete(payload.url)
        return payload.url
    }
)


const offeringPoolSlice = createSlice({
    name: 'offeringPool',
    initialState: offeringPoolAdapter.getInitialState(addOnIntitalState),
    reducers: {
        resetState(state) {
            state.api = {
                status: apiStatus.IDLE,
                error: null
            }
            state.loadedTempleIds = {}
            state.loadingTempleIds = {}
            offeringPoolAdapter.removeAll(state)
        }
    },
    extraReducers: builder => {
        builder.addCase(offeringPoolCreateThunk.pending, (state, action) => {
            state.api = {
                status: apiStatus.LOADING,
                error: null
            }
        })
        builder.addCase(offeringPoolCreateThunk.fulfilled, (state, action) => {
            state.api = {
                status: apiStatus.SUCCEEDED,
                error: null
            }
            offeringPoolAdapter.upsertOne(state, action.payload)
        })
        builder.addCase(offeringPoolCreateThunk.rejected, (state, action) => {
            state.api = {
                status: apiStatus.FAILED,
                error: action.payload
            }
        })
        builder.addCase(offeringPoolUpdateThunk.pending, (state, action) => {
            state.api = {
                status: apiStatus.LOADING,
                error: null
            }
        })
        builder.addCase(offeringPoolUpdateThunk.fulfilled, (state, action) => {
            state.api = {
                status: apiStatus.SUCCEEDED,
                error: null
            }
            offeringPoolAdapter.upsertOne(state, action.payload)
        })
        builder.addCase(offeringPoolUpdateThunk.rejected, (state, action) => {
            state.api = {
                status: apiStatus.FAILED,
                error: action.payload
            }
        })
        builder.addCase(offeringPoolListThunk.pending, (state, action) => {
            state.api = {
                status: apiStatus.LOADING,
                error: null
            }
            if (action.meta.arg.templeId) {
                state.loadingTempleIds[String(action.meta.arg.templeId)] = true
            }
        })
        builder.addCase(offeringPoolListThunk.fulfilled, (state, action) => {
            state.api = {
                status: apiStatus.SUCCEEDED,
                error: null
            }
            delete state.loadingTempleIds[String(action.payload.templeId)]
            state.loadedTempleIds[String(action.payload.templeId)] = true
            offeringPoolAdapter.upsertMany(state, action.payload.items)
        })
        builder.addCase(offeringPoolListThunk.rejected, (state, action) => {
            state.api = {
                status: apiStatus.FAILED,
                error: action.payload
            }
            if (action.meta.arg.templeId) {
                delete state.loadingTempleIds[String(action.meta.arg.templeId)]
            }
        })
        builder.addCase(offeringPoolRetrieveThunk.pending, (state, action) => {
            state.api = {
                status: apiStatus.LOADING,
                error: null
            }
        })
        builder.addCase(offeringPoolRetrieveThunk.fulfilled, (state, action) => {
            state.api = {
                status: apiStatus.SUCCEEDED,
                error: null
            }
            offeringPoolAdapter.upsertOne(state, action.payload)
        })
        builder.addCase(offeringPoolRetrieveThunk.rejected, (state, action) => {
            state.api = {
                status: apiStatus.FAILED,
                error: action.payload
            }
        })
        builder.addCase(offeringPoolDeleteThunk.pending, (state, action) => {
            state.api = {
                status: apiStatus.LOADING,
                error: null
            }
        })
        builder.addCase(offeringPoolDeleteThunk.fulfilled, (state, action) => {
            state.api = {
                status: apiStatus.SUCCEEDED,
                error: null
            }
            offeringPoolAdapter.removeOne(state, action.payload)
        })
        builder.addCase(offeringPoolDeleteThunk.rejected, (state, action) => {
            state.api = {
                status: apiStatus.FAILED,
                error: action.error.message
            }
        })
    }
})

export default offeringPoolSlice.reducer

export const { resetState } = offeringPoolSlice.actions

export const {
    selectAll: selectAllOfferingPool,
    selectById: selectOfferingPoolById,
    selectIds: selectOfferingPoolIds,
} = offeringPoolAdapter.getSelectors((state: AppState) => state.offeringPool)

export const selectOfferingPoolStatus = (state: AppState): IApiState => state.offeringPool.api

export const selectCurrentTempleOfferingPools = createSelector(
    [selectAllOfferingPool, selectCurrentTemple],
    (offeringCategories, currentTemple) =>
        offeringCategories.filter(offeringPool => offeringPool.temple === currentTemple?.url)
)
export const selectOfferingPoolsByTemple = createSelector(
    (state: AppState) => state,
    (_: any, templeUrl: string ) => templeUrl,
    (state, templeUrl) => {
        if (!templeUrl) return
        return selectAllOfferingPool(state).filter(offeringPool => offeringPool.temple === templeUrl)
    }
)

export const selectOfferingPoolEntities = (state: AppState) => state.offeringPool.entities





